import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Copy } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import api, { formatGBP, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const { wishlist, toggleWishlist, addToCart } = useCart();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [me, setMe] = useState(null);
  const [returns, setReturns] = useState([]);
  const [returnFor, setReturnFor] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then(({ data }) => setOrders(data));
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/returns/mine").then(({ data }) => setReturns(data));
  }, [user]);

  if (loading || !user) return <main className="py-32 text-center text-neutral-500">Loading…</main>;

  const onLogout = async () => { await logout(); toast.success("Signed out"); navigate("/"); };

  const submitReturn = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const note = formData.get("note");
    const returnItems = returnFor.items.map((it) => ({
      product_id: it.product_id, name: it.name,
      quantity: parseInt(formData.get(`qty_${it.product_id}`) || 0),
      reason: formData.get(`reason_${it.product_id}`) || "",
    })).filter((i) => i.quantity > 0);
    if (returnItems.length === 0) return toast.error("Select at least one item to return");
    try {
      await api.post("/returns", { order_id: returnFor.id, items: returnItems, note });
      toast.success("Return request submitted. We'll be in touch shortly.");
      setReturnFor(null);
      const { data } = await api.get("/returns/mine"); setReturns(data);
      setParams({ tab: "returns" });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyReferral = () => {
    const code = me?.referral_code;
    if (!code) return;
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  return (
    <main className="max-w-[1480px] mx-auto px-6 md:px-12 py-14" data-testid="account-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <p className="eyebrow text-neutral-600">Hello, {user.name}</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-2">Your Account</h1>
        </div>
        <div className="flex gap-4">
          {user.role === "admin" && <Link to="/admin" className="btn-ghost" data-testid="admin-link">Admin Panel →</Link>}
          <button onClick={onLogout} className="btn-ghost" data-testid="logout-btn">Sign out</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-2 border-b border-hair mb-10">
        {[
          { k: "orders", l: "Orders" },
          { k: "wishlist", l: "Wishlist" },
          { k: "rewards", l: "Rewards & Referrals" },
          { k: "returns", l: "Returns" },
          { k: "profile", l: "Profile" },
        ].map((t) => (
          <button key={t.k} onClick={() => setParams({ tab: t.k })} data-testid={`tab-${t.k}`}
            className={`pb-4 text-sm tracking-[0.18em] uppercase ${tab === t.k ? "border-b-2 border-[var(--gold)] text-[var(--gold)]" : "text-neutral-600"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "orders" && (
        <section data-testid="orders-tab">
          {orders.length === 0 ? (
            <p className="text-neutral-500 text-sm">No orders yet. <Link to="/shop" className="underline">Discover the collection</Link>.</p>
          ) : orders.map((o) => (
            <div key={o.id} className="border border-hair p-6 mb-4" data-testid={`order-${o.id}`}>
              <div className="flex justify-between mb-3 text-sm">
                <span className="font-medium">Order #{o.id.slice(0, 8).toUpperCase()}</span>
                <span className="eyebrow text-neutral-600">{o.status}</span>
              </div>
              <p className="text-xs text-neutral-500">Placed {new Date(o.created_at).toLocaleDateString("en-GB")}</p>
              <div className="rule my-4" />
              {o.items.map((it) => (
                <div key={it.product_id} className="flex justify-between text-sm py-1">
                  <span>{it.name} × {it.quantity}</span>
                  <span>{formatGBP(it.price * it.quantity)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm mt-3 pt-3 border-t border-hair">
                <span className="text-neutral-600">Total</span>
                <span className="font-serif text-base">{formatGBP(o.total)}</span>
              </div>
              {(o.tracking_number || o.awb_number || (o.timeline && o.timeline.length > 0)) && (
                <div className="mt-4 pt-4 border-t border-hair" data-testid={`order-tracking-${o.id}`}>
                  <p className="eyebrow text-neutral-600 text-[10px] mb-2">Delivery</p>
                  {o.shipping_carrier && (
                    <p className="text-xs text-neutral-600">Carrier: <span className="font-medium">{o.shipping_carrier}</span></p>
                  )}
                  {o.tracking_number && (
                    <p className="text-xs text-neutral-600 mt-1">
                      Tracking: <Link to={`/track?n=${o.tracking_number}`} className="underline">{o.tracking_number}</Link>
                      {o.tracking_url && (
                        <> · <a href={o.tracking_url} target="_blank" rel="noreferrer" className="underline">Track parcel ↗</a></>
                      )}
                    </p>
                  )}
                  {o.awb_number && (
                    <p className="text-xs text-neutral-600 mt-1">Air Waybill (AWB): <span className="font-mono">{o.awb_number}</span></p>
                  )}
                  {o.timeline && o.timeline.length > 0 && (
                    <ol className="mt-3 space-y-1.5">
                      {o.timeline.slice().reverse().map((t, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--gold)] flex-shrink-0" />
                          <span className="text-neutral-700">{t.label}</span>
                          <span className="text-neutral-400 ml-auto">{t.at ? new Date(t.at).toLocaleDateString("en-GB") : ""}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
              {o.payment_status === "paid" && (
                <button onClick={() => setReturnFor(o)} className="btn-ghost mt-4" data-testid={`request-return-${o.id}`}>Request a return</button>
              )}
            </div>
          ))}
        </section>
      )}

      {tab === "wishlist" && (
        <section data-testid="wishlist-tab">
          {wishlist.length === 0 ? (
            <p className="text-neutral-500 text-sm">Nothing saved yet.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {wishlist.map((p) => (
                <div key={p.id} className="rise" data-testid={`wish-${p.id}`}>
                  <Link to={`/product/${p.id}`}><div className="aspect-[4/5] bg-sand"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div></Link>
                  <p className="font-serif text-lg mt-4">{p.name}</p>
                  <p className="text-sm">{formatGBP(p.price)}</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => addToCart(p)} className="btn-ghost">Add to bag</button>
                    <button onClick={() => toggleWishlist(p)} className="text-xs text-neutral-500 hover:text-[var(--gold)]">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "rewards" && me && (
        <section data-testid="rewards-tab" className="grid md:grid-cols-2 gap-10 max-w-4xl">
          <div className="border border-hair p-8">
            <p className="eyebrow text-neutral-600">Loyalty Points</p>
            <p className="font-serif text-6xl mt-3">{me.loyalty_points || 0}</p>
            <p className="text-sm text-neutral-500 mt-3">Earn 1 point for every £1 spent. 100 points = £1 off your next order.</p>
            <p className="text-xs text-neutral-500 mt-2">Apply at checkout in the Loyalty Points slider.</p>
          </div>
          <div className="border border-hair p-8">
            <p className="eyebrow text-neutral-600">Refer a Friend</p>
            <p className="font-serif text-2xl mt-3">Give 10%, get 10%</p>
            <p className="text-sm text-neutral-500 mt-3">Share your code. When a friend signs up and shops, you both get 10% off.</p>
            <div className="mt-5 flex items-center gap-2">
              <code className="bg-sand px-4 py-3 text-sm font-mono flex-1" data-testid="referral-code">{me.referral_code}</code>
              <button onClick={copyReferral} className="btn-ghost" data-testid="copy-referral"><Copy className="w-4 h-4" strokeWidth={1.25} /></button>
            </div>
          </div>
        </section>
      )}

      {tab === "returns" && (
        <section data-testid="returns-tab">
          {returns.length === 0 ? (
            <p className="text-neutral-500 text-sm">No return requests yet.</p>
          ) : returns.map((r) => (
            <div key={r.id} className="border border-hair p-6 mb-4" data-testid={`return-${r.id}`}>
              <div className="flex justify-between mb-3 text-sm">
                <span className="font-medium">Return #{r.id.slice(0,8).toUpperCase()}</span>
                <span className="eyebrow text-[var(--gold)]">{r.status}</span>
              </div>
              <p className="text-xs text-neutral-500">For order #{r.order_id.slice(0,8).toUpperCase()} · {new Date(r.created_at).toLocaleDateString("en-GB")}</p>
              <div className="rule my-3" />
              {r.items.map((it, i) => <p key={i} className="text-sm py-1">{it.name} × {it.quantity} — <span className="text-neutral-500">{it.reason}</span></p>)}
              {r.admin_note && <p className="text-xs text-neutral-600 mt-3 italic">Note from us: {r.admin_note}</p>}
            </div>
          ))}
        </section>
      )}

      {tab === "profile" && (
        <section data-testid="profile-tab" className="max-w-md space-y-3 text-sm">
          <div><p className="eyebrow text-neutral-600">Name</p><p className="mt-1">{user.name}</p></div>
          <div><p className="eyebrow text-neutral-600">Email</p><p className="mt-1">{user.email}</p></div>
          <div><p className="eyebrow text-neutral-600">Role</p><p className="mt-1 capitalize">{user.role}</p></div>
        </section>
      )}

      {returnFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-auto py-10" onClick={() => setReturnFor(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitReturn} className="bg-white p-8 max-w-xl w-full" data-testid="return-modal">
            <h3 className="font-serif text-2xl mb-1">Return request</h3>
            <p className="text-xs text-neutral-500 mb-6">Order #{returnFor.id.slice(0,8).toUpperCase()}</p>
            {returnFor.items.map((it) => (
              <div key={it.product_id} className="border-t border-hair py-3">
                <p className="font-serif text-base">{it.name}</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <input name={`qty_${it.product_id}`} type="number" min="0" max={it.quantity} placeholder={`Qty (max ${it.quantity})`} defaultValue={0} className="border border-hair px-3 py-2 text-sm" />
                  <select name={`reason_${it.product_id}`} className="border border-hair px-3 py-2 text-sm">
                    <option value="">Reason…</option>
                    <option value="not_as_expected">Not as expected</option>
                    <option value="wrong_size">Wrong size</option>
                    <option value="damaged">Arrived damaged</option>
                    <option value="changed_mind">Changed my mind</option>
                  </select>
                </div>
              </div>
            ))}
            <textarea name="note" placeholder="Anything we should know?" rows={3} className="w-full border border-hair px-3 py-2 text-sm mt-4" />
            <div className="flex gap-3 mt-5">
              <button type="submit" className="btn-primary" data-testid="return-submit">Submit Request</button>
              <button type="button" onClick={() => setReturnFor(null)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
